/* ===========================================================================
  Translates kit variant-picker events into Turbo's `variant-change` CustomEvent,
  which `window.productPage.runOptionSelector` (sections.js) listens for.
============================================================================ */

// eslint-disable-next-line import/no-unresolved
import 'kit.product-variant-picker';

const INLINE_QUICKSHOP_SECTION = 'inline-quickshop';

function retargetInlineQuickshop(form) {
  if (!form.closest('.inline-quickshop')) return;

  const picker = form.querySelector('kit-variant-picker');

  if (!picker) return;

  picker.dataset.sectionId = INLINE_QUICKSHOP_SECTION;
}

function readProductPayload(form) {
  try {
    return JSON.parse(form?.dataset.product ?? 'null');
  } catch (error) {
    console.error('Could not parse the product payload.', error);
    return null;
  }
}

function disableWhileInFlight(form) {
  form.querySelectorAll('.add_to_cart').forEach(addToCart => {
    addToCart.classList.add('disabled');
    addToCart.setAttribute('disabled', 'disabled');
    addToCart.setAttribute('aria-disabled', 'true');
  });
}

// Kit's radio has no tooltip slot, so the theme applies the legacy
// `.swatch .tooltip` from the value name kit renders visually hidden.
const tooltipObserved = new WeakSet();

function applySwatchTooltips(form) {
  form
    .querySelectorAll('kit-variant-picker [data-option-value-id]:not([data-swatch-tooltip])')
    .forEach(label => {
      if (!label.querySelector('.kit-swatch')) return;

      const text = label.querySelector('.kit-visually-hidden')?.textContent.trim();

      if (text) label.setAttribute('data-swatch-tooltip', text);
    });
}

// Re-applied because kit swaps the picker's innerHTML on every selection.
function watchSwatchTooltips(form) {
  applySwatchTooltips(form);

  if (tooltipObserved.has(form)) return;

  tooltipObserved.add(form);

  new MutationObserver(() => applySwatchTooltips(form))
    .observe(form, { childList: true, subtree: true });
}

function read(form) {
  retargetInlineQuickshop(form);
  watchSwatchTooltips(form);

  const product = readProductPayload(form);
  const blob = form?.querySelector('[data-kit-selected-variant]');
  let variant = product?.variants?.[0] ?? null;

  if (blob) {
    try {
      variant = JSON.parse(blob.textContent?.trim() ?? 'null');
    } catch (error) {
      console.error('Could not parse the selected variant JSON.', error);
    }
  }

  return { product, variant };
}

window.kitVariantPicker = {
  read,
};

if (!window.__kitVariantPickerBridge) {
  window.__kitVariantPickerBridge = true;

  const settle = (form, picker, variant) => {
    const image = variant && variant.featured_image && variant.featured_image.src;

    if (image) window.swapCardImage(picker.closest('.thumbnail'), image);

    form.dispatchEvent(new CustomEvent('variant-change', {
      bubbles: true,
      detail: {
        product: readProductPayload(form),
        variant,
        selectionIncomplete: picker.selectionIncomplete,
      },
    }));
  };

  document.addEventListener('kit:variant-picker:change', event => {
    const form = event.target.closest('[data-product-form]');

    if (form) disableWhileInFlight(form);
  });

  document.addEventListener('kit:variant-picker:after-change', event => {
    const form = event.target.closest('[data-product-form]');

    if (!form) return;

    const { variant, productChanged } = event.detail;
    const picker = event.target;

    // Combined listings
    if (productChanged) {
      const productUrl = new URL(picker.dataset.productUrl, window.location.origin);
      const targetUrl = window.location.pathname.includes('/products/')
        ? new URL(window.location.href)
        : productUrl;

      targetUrl.pathname = productUrl.pathname;

      if (picker.dataset.variantId) {
        targetUrl.searchParams.set('variant', picker.dataset.variantId);
      }

      window.location.assign(targetUrl.toString());
      return;
    }

    settle(form, picker, variant);
  });

  document.addEventListener('kit:variant-picker:error', event => {
    const form = event.target.closest('[data-product-form]');

    if (!form) return;

    console.error('Variant change failed:', event.detail.error);

    // Kit's default is to navigate to the failed selection's URL. Quick shop
    // (modal or inline) has nowhere to navigate to — settle as unavailable
    // instead of leaving the shopper on a dead page.
    if (event.target.closest('.js-quick-shop, .inline-quickshop')) {
      event.preventDefault();
      settle(form, event.target, null);
    }
  });
}
